import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LogIn, UserPlus, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import LoadingButton from "../components/LoadingButton";

const HERO_BG = `${process.env.PUBLIC_URL || ""}/assets/hero-login.png`;

export default function LoginPage() {
  const { login, config } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(identifier, password);
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    nav(res.user.role === "admin" ? "/admin" : "/dashboard");
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      {/* Hero */}
      <section
        className="relative overflow-hidden text-white px-6 sm:px-10 py-12 sm:py-20 lg:min-h-screen lg:flex lg:flex-col lg:justify-between"
        style={{ background: `linear-gradient(140deg, rgba(11,110,110,0.94) 0%, rgba(11,110,110,0.86) 60%), url(${HERO_BG}) center/cover` }}
      >
        <div className="flex items-center gap-3">
          {config?.logo ? (
            <img src={config.logo} alt="logo" className="w-12 h-12 rounded-2xl object-cover ring-1 ring-white/30" />
          ) : (
            <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center font-heading font-extrabold text-brand text-xl">K</div>
          )}
          <div>
            <div className="font-heading font-extrabold text-lg">{config?.name || "Kopsebra"}</div>
            <div className="text-xs uppercase tracking-[0.22em] text-white/65">Sistem Manajemen Koperasi</div>
          </div>
        </div>

        <div className="mt-12 lg:mt-0 max-w-xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/20 px-3 py-1 text-xs">
            <Sparkles size={14} /> Modern, aman, terintegrasi
          </div>
          <h1 className="mt-5 font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
            Kelola Keuangan <span className="text-gold">Koperasi</span> dengan Mudah
          </h1>
          <p className="mt-5 text-white/80 text-base sm:text-lg max-w-md">
            Platform digital untuk simpan-pinjam, laporan keuangan, dan administrasi anggota koperasi
            secara modern dan transparan.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-sm">
            {[
              { k: "Aman", v: "Terenkripsi" },
              { k: "Mobile", v: "Responsif" },
              { k: "Real-time", v: "Laporan" },
            ].map((it) => (
              <div key={it.k} className="rounded-2xl bg-white/8 ring-1 ring-white/15 p-3">
                <div className="font-heading font-bold">{it.k}</div>
                <div className="text-[11px] text-white/65">{it.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden lg:block text-xs text-white/50">© {new Date().getFullYear()} {config?.name || "Kopsebra"}. Semua hak dilindungi.</div>
      </section>

      {/* Login form */}
      <section className="bg-mint-pattern px-5 sm:px-10 py-10 lg:min-h-screen flex items-center">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-6">
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">
              Selamat Datang Kembali
            </h2>
            <p className="text-brand-muted mt-2 text-sm sm:text-base">
              Masukkan email atau NID beserta password untuk mengakses akun Anda.
            </p>
          </div>

          <form onSubmit={submit} className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-brand/10 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold text-brand-ink mb-2">Email atau NID</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="email@contoh.com / NID"
                className="w-full rounded-2xl border border-brand/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 bg-white"
                required
                data-testid="login-identifier-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-brand-ink mb-2">Password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-2xl border border-brand/20 px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 bg-white"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand"
                  data-testid="login-toggle-password"
                  aria-label="toggle password visibility"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3" data-testid="login-error">
                {error}
              </div>
            )}

            <LoadingButton
              type="submit"
              loading={loading}
              loadingText="Memproses…"
              data-testid="login-submit-btn"
              className="w-full rounded-full bg-brand text-white py-3 font-semibold text-sm hover:bg-brand-hover"
            >
              <LogIn size={16} /> Masuk ke Dashboard
            </LoadingButton>

            <div className="flex items-center gap-3 text-xs text-brand-muted">
              <div className="h-px bg-brand/15 flex-1" />
              atau belum punya akun?
              <div className="h-px bg-brand/15 flex-1" />
            </div>

            <Link
              to="/register"
              data-testid="login-register-link"
              className="w-full rounded-full border-2 border-brand/30 text-brand py-3 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-brand-soft transition"
            >
              <UserPlus size={16} /> Registrasi Anggota
            </Link>
          </form>
        </div>
      </section>
    </div>
  );
}
